import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { JOB_CATEGORIES } from "@/lib/i18n";
import { MapPin, Briefcase, DollarSign, ExternalLink } from "lucide-react";
import { FavoriteButton } from "@/components/FavoriteButton";
import { ShareToCommunityButton } from "@/components/community/ShareToCommunity";

export default function Jobs() {
  const { t } = useLang();
  const [params, setParams] = useSearchParams();
  const city = params.get("city") || "";
  const category = params.get("category") || "";
  const [all, setItems] = useState([]);
  const focusId = params.get("id") || "";
  const items = focusId ? all.filter(x => x.id === focusId) : all;
  const [cities, setCities] = useState([]);

  useEffect(() => { api.get("/cities").then(r => setCities(r.data)); }, []);
  useEffect(() => {
    const q = {};
    if (city) q.city = city;
    if (category) q.category = category;
    api.get("/jobs", { params: q }).then(r => setItems(r.data));
  }, [city, category]);

  const setP = (k, v) => {
    const p = new URLSearchParams(params);
    v ? p.set(k, v) : p.delete(k);
    setParams(p);
  };

  return (
    <div className="page fade-up">
      <div className="pt-4 pb-2 flex items-center justify-between">
        <h1 className="font-display text-2xl font-extrabold">{t("jobs")}</h1>
        <Link to="/publicar?type=job" data-testid="jobs-publish-btn"
              className="text-xs font-semibold px-3 py-2 rounded-full bg-[#c8102e] text-white hover:bg-[#a50c24] transition-colors">
          + {t("publish_job")}
        </Link>
      </div>

      <div className="flex overflow-x-auto gap-2 pb-2 -mx-4 px-4 scrollbar-none mb-2">
        <button onClick={() => setP("category", "")} className="chip whitespace-nowrap" data-active={!category}>{t("all_categories")}</button>
        {JOB_CATEGORIES.map(c => (
          <button key={c} onClick={() => setP("category", c)} className="chip whitespace-nowrap" data-active={category === c}>{c}</button>
        ))}
      </div>
      <div className="flex overflow-x-auto gap-2 pb-2 -mx-4 px-4 scrollbar-none mb-4">
        <button onClick={() => setP("city", "")} className="chip whitespace-nowrap" data-active={!city}>{t("all_cities")}</button>
        {cities.map(c => (
          <button key={c.name} onClick={() => setP("city", c.name)} className="chip whitespace-nowrap" data-active={city === c.name}>{c.name}</button>
        ))}
      </div>

      <div className="space-y-2 mb-8">
        {focusId && <button onClick={() => setP("id", "")} data-testid="focus-clear" className="col-span-2 w-full text-xs text-zinc-600 underline text-left">← Ver todos</button>}
        {items.length === 0 && <p className="text-sm text-zinc-500 italic text-center py-8">Nenhuma vaga encontrada.</p>}
        {items.map(j => (
          <div key={j.id} data-testid={`job-item-${j.id}`}
               className="bg-white rounded-2xl border border-zinc-200 p-4 card-hover">
            <div className="flex items-start justify-between gap-2 mb-1">
              <h3 className="font-display font-bold text-base leading-tight flex-1">{j.title}</h3>
              <ShareToCommunityButton type="job" id={j.id} testid={`job-share-community-${j.id}`} />
              <FavoriteButton type="job" id={j.id} />
              
            </div>
            <div className="text-xs text-zinc-500 mb-2 flex items-center gap-1"><Briefcase size={11} /> {j.company}</div>
            <div className="flex flex-wrap gap-2 text-[11px] text-zinc-600 mb-2">
              <span className="chip !py-1 !text-[11px]"><MapPin size={11} /> {j.city}</span>
              <span className="chip !py-1 !text-[11px]">{j.category}</span>
              {j.salary && <span className="chip !py-1 !text-[11px] !bg-yellow-50 !border-yellow-300"><DollarSign size={11} /> {j.salary}</span>}
            </div>
            <p className="text-xs text-zinc-700 leading-relaxed mb-2">{j.description}</p>
            <div className="flex gap-2">
              {j.apply_link && (
                <a href={j.apply_link} target="_blank" rel="noreferrer"
                   className="flex-1 flex items-center justify-center gap-1 py-2 rounded-lg bg-[#c8102e] hover:bg-[#a50c24] text-white text-xs font-semibold transition-colors">
                  Candidatar <ExternalLink size={11} />
                </a>
              )}
              {j.contact && (
                <a href={j.contact.includes("@") ? `mailto:${j.contact}` : `tel:${j.contact}`}
                   className="flex-1 flex items-center justify-center py-2 rounded-lg bg-black hover:bg-zinc-800 text-white text-xs font-semibold transition-colors">
                  Contactar
                </a>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
